/**
 * SyncWorkerRunner — instantiates the SyncWorker and triggers a drain when
 * connectivity returns or when the page becomes visible.
 *
 * Mounted once at the App root. The component renders nothing.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import {
  SyncWorker,
  SYNC_COMPLETED_EVENT,
  type SyncCompletedDetail,
} from "./syncWorker";

function readAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("access_token");
  } catch {
    return null;
  }
}

export function SyncWorkerRunner() {
  const queryClient = useQueryClient();
  const { isOnline } = useOnlineStatus();
  const workerRef = useRef<SyncWorker | null>(null);
  const wasOnlineRef = useRef<boolean | null>(null);
  const { t } = useTranslation("sync");

  if (workerRef.current === null) {
    workerRef.current = new SyncWorker({
      queryClient,
      getAccessToken: readAccessToken,
    });
  }

  // Drain whenever we transition from offline → online.
  useEffect(() => {
    if (wasOnlineRef.current === false && isOnline && workerRef.current) {
      workerRef.current.drain();
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline]);

  // Initial drain on mount if we're online (handles "user returned to a tab
  // that had a stale queue from a previous session").
  useEffect(() => {
    if (isOnline && workerRef.current) {
      workerRef.current.drain();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface a toast on completion.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onCompleted(event: Event) {
      const detail = (event as CustomEvent<SyncCompletedDetail>).detail;
      if (!detail) return;
      if (detail.successCount > 0) {
        toast.success(
          t("sync_complete.toast_other", {
            count: detail.successCount,
            defaultValue: "{{count}} entries synced",
          }),
        );
      }
      if (detail.failedCount > 0) {
        toast.error(
          t("sync_failed.toast_other", {
            count: detail.failedCount,
            defaultValue:
              "Could not sync {{count}} entries. They will retry automatically.",
          }),
        );
      }
    }
    window.addEventListener(SYNC_COMPLETED_EVENT, onCompleted);
    return () => window.removeEventListener(SYNC_COMPLETED_EVENT, onCompleted);
  }, [t]);

  return null;
}
